// Pricing calculation utility for CRAFTFINDUS Handwerker-Lead Platform
// Formula: Base(Category) × UrgencyMultiplier × ComplexityMultiplier + Surcharges

export interface PricingConfig {
  basePrices: {
    support: number;
    project_small: number;
    project_medium: number;
    project_large: number;
    managed_service: number;
    security: number;
    cloud: number;
  };
  urgencyMultipliers: {
    immediate: number;
    within_48h: number;
    within_7_days: number;
    planned: number;
  };
  complexityMultipliers: {
    low: number;
    medium: number;
    high: number;
  };
  surcharges: {
    compliance: number;
    onsite: number;
  };
  roundingEnabled: boolean;
}

export const DEFAULT_PRICING_CONFIG: PricingConfig = {
  basePrices: {
    support: 39,
    project_small: 119,
    project_medium: 199,
    project_large: 399,
    managed_service: 249,
    security: 299,
    cloud: 199,
  },
  urgencyMultipliers: {
    immediate: 1.40,
    within_48h: 1.20,
    within_7_days: 1.00,
    planned: 0.90,
  },
  complexityMultipliers: {
    low: 1.00,
    medium: 1.15,
    high: 1.30,
  },
  surcharges: {
    compliance: 79,
    onsite: 49,
  },
  roundingEnabled: true,
};

export type RequestCategory = 'managed_service' | 'project' | 'security' | 'cloud' | 'support';
export type RequestUrgency = 'immediate' | 'within_48h' | 'within_7_days' | 'planned';
export type ProjectSize = 'small' | 'medium' | 'large';
export type ComplexityLevel = 'low' | 'medium' | 'high';

export interface StackTag {
  id: string;
  name: string;
  is_advanced: boolean;
}

export interface PricingInput {
  category: RequestCategory;
  urgency: RequestUrgency;
  stackTags: StackTag[];
  complianceRelevant: boolean;
  onsiteRequired: boolean;
  projectUsersRange?: string;
  projectSystemsRange?: string;
}

/**
 * Derive project size from user and system count questions
 */
export function deriveProjectSize(
  usersRange?: string,
  systemsRange?: string
): ProjectSize {
  // Mapping:
  // klein: (1–10 users & 1–3 systems) OR (11–50 users & 1 system)
  // groß: (200+ users) OR (10+ systems)
  // mittel: everything else

  const userMapping: Record<string, number> = {
    '1-10': 1,
    '11-50': 2,
    '51-200': 3,
    '200+': 4,
  };

  const systemMapping: Record<string, number> = {
    '1': 1,
    '2-3': 2,
    '4-10': 3,
    '10+': 4,
  };

  const userLevel = userMapping[usersRange || ''] || 2;
  const systemLevel = systemMapping[systemsRange || ''] || 2;

  // Large: 200+ users OR 10+ systems
  if (userLevel >= 4 || systemLevel >= 4) {
    return 'large';
  }

  // Small: (1-10 users AND 1-3 systems) OR (11-50 users AND 1 system)
  if ((userLevel === 1 && systemLevel <= 2) || (userLevel === 2 && systemLevel === 1)) {
    return 'small';
  }

  return 'medium';
}

/**
 * Calculate complexity level based on stack tags
 */
export function calculateComplexity(stackTags: StackTag[]): ComplexityLevel {
  const tagCount = stackTags.length;
  const hasAdvanced = stackTags.some(tag => tag.is_advanced);

  // High: 6+ tags OR any advanced tag with 3+ total
  if (tagCount >= 6 || (hasAdvanced && tagCount >= 3)) {
    return 'high';
  }

  // Medium: 3-5 tags OR has advanced tag
  if (tagCount >= 3 || hasAdvanced) {
    return 'medium';
  }

  // Low: 1-2 standard tags
  return 'low';
}

/**
 * Round price to nearest 9 (e.g., 121 → 129, 206 → 209)
 */
export function roundToNearest9(price: number): number {
  const base = Math.floor(price / 10) * 10;
  return base + 9;
}

/**
 * Get base price for a category, considering project size for projects
 */
export function getBasePrice(
  category: RequestCategory,
  projectSize: ProjectSize,
  config: PricingConfig = DEFAULT_PRICING_CONFIG
): number {
  if (category === 'project') {
    return config.basePrices[`project_${projectSize}`];
  }

  const categoryMap: Record<RequestCategory, keyof typeof config.basePrices> = {
    support: 'support',
    managed_service: 'managed_service',
    security: 'security',
    cloud: 'cloud',
    project: 'project_medium',
  };

  return config.basePrices[categoryMap[category]];
}

/**
 * Main pricing calculation function
 */
export function calculatePrice(
  input: PricingInput,
  config: PricingConfig = DEFAULT_PRICING_CONFIG
): number {
  // 1. Get base price
  const projectSize = input.category === 'project'
    ? deriveProjectSize(input.projectUsersRange, input.projectSystemsRange)
    : 'medium';
  
  const basePrice = getBasePrice(input.category, projectSize, config);

  // 2. Apply urgency multiplier
  const urgencyMultiplier = config.urgencyMultipliers[input.urgency];

  // 3. Calculate complexity and apply multiplier
  const complexity = calculateComplexity(input.stackTags);
  const complexityMultiplier = config.complexityMultipliers[complexity];

  // 4. Calculate base with multipliers
  let price = basePrice * urgencyMultiplier * complexityMultiplier;

  // 5. Add surcharges
  if (input.complianceRelevant) {
    price += config.surcharges.compliance;
  }
  if (input.onsiteRequired) {
    price += config.surcharges.onsite;
  }

  // 6. Round to nearest 9 if enabled
  if (config.roundingEnabled) {
    price = roundToNearest9(price);
  }

  return price;
}

/**
 * Format price for display with € symbol
 */
export function formatPrice(price: number): string {
  return `${Math.round(price)} €`;
}

/**
 * Generate price breakdown for transparency
 */
export function getPriceBreakdown(
  input: PricingInput,
  config: PricingConfig = DEFAULT_PRICING_CONFIG
): {
  basePrice: number;
  urgencyMultiplier: number;
  complexityMultiplier: number;
  complexity: ComplexityLevel;
  complianceSurcharge: number;
  onsiteSurcharge: number;
  subtotal: number;
  finalPrice: number;
} {
  const projectSize = input.category === 'project'
    ? deriveProjectSize(input.projectUsersRange, input.projectSystemsRange)
    : 'medium';

  const basePrice = getBasePrice(input.category, projectSize, config);
  const urgencyMultiplier = config.urgencyMultipliers[input.urgency];
  const complexity = calculateComplexity(input.stackTags);
  const complexityMultiplier = config.complexityMultipliers[complexity];

  const subtotal = basePrice * urgencyMultiplier * complexityMultiplier;
  const complianceSurcharge = input.complianceRelevant ? config.surcharges.compliance : 0;
  const onsiteSurcharge = input.onsiteRequired ? config.surcharges.onsite : 0;

  let finalPrice = subtotal + complianceSurcharge + onsiteSurcharge;
  if (config.roundingEnabled) {
    finalPrice = roundToNearest9(finalPrice);
  }

  return {
    basePrice,
    urgencyMultiplier,
    complexityMultiplier,
    complexity,
    complianceSurcharge,
    onsiteSurcharge,
    subtotal,
    finalPrice,
  };
}
